/**
 * Searchable dropdown for the orientation editor: the field itself is the
 * type-to-filter input (same interaction as the asset picker in the platform
 * webclient). Click or focus opens the full list with the current value
 * highlighted; typing filters it (case-insensitive substring, prefix matches
 * ranked first); Enter / click picks, Escape or blur reverts to the selected
 * label. Options are {value, label} pairs so callers can decorate labels
 * (command names, stale-twin suffixes) without changing the stored value.
 *
 * Implementation follows OccupationSelect / EmailSuggestInput: a custom
 * position-fixed listbox anchored to the input's bounding rect, because the
 * inspector body scrolls and the details modal clips overflow, and native
 * datalist popups misposition inside the Electron/webview host. The list
 * flips above the input when the viewport below is too short (the inspector
 * card usually floats near the bottom edge).
 */
import {
  useEffect,
  useId,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type KeyboardEvent,
} from 'react';
import { Icon } from '../Icon';
import { PortalIcons } from '../portalIcons';

export type SearchSelectOption = {
  value: string;
  label: string;
  /** Optional trailing chip, styled as `oe-status <badge lowercased>`. */
  badge?: string;
  /** Optional hover text for the option row (native title tooltip). */
  title?: string;
};

/** Cap the floating list height; it scrolls past this. */
const MAX_LIST_HEIGHT = 280;
/** Minimum room below the input before the list flips above it. */
const MIN_SPACE = 160;

export function SearchSelect({
  value,
  options,
  onChange,
  disabled,
  placeholder,
  variant = 'inp',
}: {
  value: string;
  options: SearchSelectOption[];
  onChange: (v: string) => void;
  disabled?: boolean;
  placeholder?: string;
  /** 'inp' = inspector field chrome (.oe-inp), 'select' = header chrome (.oe-select). */
  variant?: 'inp' | 'select';
}) {
  const wrapperRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLDivElement>(null);
  const [open, setOpen] = useState(false);
  // null = not typing: the input shows the selected label and the list is
  // unfiltered. A string (even '') = live query.
  const [query, setQuery] = useState<string | null>(null);
  const [activeIdx, setActiveIdx] = useState(-1);
  const [anchorRect, setAnchorRect] = useState<DOMRect | null>(null);
  const listId = useId();

  const selected = options.find((o) => o.value === value);
  const selectedLabel = selected?.label ?? value;

  const filtered = useMemo<SearchSelectOption[]>(() => {
    const q = (query ?? '').trim().toLowerCase();
    if (!q) return options;
    return options
      .map((o) => {
        const label = o.label.toLowerCase();
        const val = o.value.toLowerCase();
        const badge = (o.badge ?? '').toLowerCase();
        const score =
          label.startsWith(q) || val.startsWith(q) ? 0
          : label.includes(q) || val.includes(q) || badge.startsWith(q) ? 1
          : -1;
        return { o, score };
      })
      .filter((r) => r.score >= 0)
      .sort((a, b) => a.score - b.score || a.o.label.localeCompare(b.o.label))
      .map((r) => r.o);
  }, [options, query]);

  // While typing, keep the first match highlighted; on plain open, highlight
  // the current value so the list opens "at" it like a native select.
  // Only on open/query change: callers pass a fresh `options` array every
  // render, and re-snapping on each one yanked a wheel-scrolled list back to
  // the selected row whenever the parent re-rendered.
  useEffect(() => {
    if (!open) return;
    if (query != null) {
      setActiveIdx(filtered.length > 0 ? 0 : -1);
    } else {
      setActiveIdx(Math.max(0, filtered.findIndex((o) => o.value === value)));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, query]);

  // Keep the highlighted row visible as arrow keys walk a long, scrolled list.
  useEffect(() => {
    if (!open || activeIdx < 0) return;
    listRef.current
      ?.querySelector<HTMLElement>(`[data-idx="${activeIdx}"]`)
      ?.scrollIntoView({ block: 'nearest' });
  }, [activeIdx, open]);

  // Measure the input on open and on layout shifts (panel drag, body scroll,
  // window resize) so the fixed-position list tracks it. useLayoutEffect so
  // it positions before paint on the first open frame.
  useLayoutEffect(() => {
    if (!open) return;
    const measure = () =>
      setAnchorRect(inputRef.current?.getBoundingClientRect() ?? null);
    measure();
    window.addEventListener('resize', measure);
    window.addEventListener('scroll', measure, true);
    return () => {
      window.removeEventListener('resize', measure);
      window.removeEventListener('scroll', measure, true);
    };
  }, [open, filtered.length]);

  // Outside-click closes. The list is a DOM descendant of the wrapper even
  // though it's position:fixed, so `contains` covers clicks on rows too.
  useEffect(() => {
    if (!open) return;
    function onDown(e: MouseEvent) {
      const target = e.target as Node | null;
      if (target && wrapperRef.current?.contains(target)) return;
      close();
    }
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [open]);

  function close() {
    setOpen(false);
    setQuery(null);
  }

  function pick(opt: SearchSelectOption) {
    onChange(opt.value);
    close();
  }

  function onKeyDown(e: KeyboardEvent<HTMLInputElement>) {
    if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
      e.preventDefault();
      if (!open) {
        setOpen(true);
        return;
      }
      if (filtered.length) {
        const d = e.key === 'ArrowDown' ? 1 : -1;
        setActiveIdx((i) => (i + d + filtered.length) % filtered.length);
      }
    } else if (e.key === 'Enter') {
      if (open && activeIdx >= 0 && filtered[activeIdx]) {
        e.preventDefault();
        pick(filtered[activeIdx]);
      }
    } else if (e.key === 'Escape') {
      if (open) {
        e.preventDefault();
        close();
      }
    } else if (e.key === 'Tab') {
      close();
    }
  }

  // Flip above the input when the space below is too short, and cap the list
  // to whatever room the chosen side actually has.
  const openUp =
    anchorRect != null &&
    window.innerHeight - anchorRect.bottom < MIN_SPACE &&
    anchorRect.top > window.innerHeight - anchorRect.bottom;
  const maxHeight = anchorRect
    ? Math.min(
        MAX_LIST_HEIGHT,
        (openUp ? anchorRect.top : window.innerHeight - anchorRect.bottom) - 12,
      )
    : MAX_LIST_HEIGHT;

  return (
    <div className="oe-combo" ref={wrapperRef}>
      <input
        ref={inputRef}
        className={variant === 'select' ? 'oe-select' : 'oe-inp'}
        value={query ?? selectedLabel}
        placeholder={placeholder ?? selectedLabel}
        disabled={disabled}
        autoComplete="off"
        spellCheck={false}
        role="combobox"
        aria-autocomplete="list"
        aria-expanded={open}
        aria-controls={listId}
        onChange={(e) => {
          setQuery(e.target.value);
          if (!open) setOpen(true);
        }}
        onFocus={(e) => {
          // Select-all so the first keystroke replaces the label with the query.
          e.target.select();
          setOpen(true);
        }}
        onMouseDown={() => {
          // Reopen on click even when the input already has focus.
          if (!open) setOpen(true);
        }}
        onBlur={close}
        onKeyDown={onKeyDown}
      />
      <span className="oe-combo-caret" aria-hidden="true"><Icon of={PortalIcons.action.expand} size={12} /></span>
      {open && anchorRect && (
        <div
          ref={listRef}
          id={listId}
          role="listbox"
          className="oe-combo-list"
          style={{
            top: openUp ? undefined : anchorRect.bottom + 4,
            bottom: openUp ? window.innerHeight - anchorRect.top + 4 : undefined,
            left: anchorRect.left,
            minWidth: anchorRect.width,
            maxHeight,
          }}
        >
          {filtered.length === 0 ? (
            <div className="oe-combo-empty">No matches</div>
          ) : (
            filtered.map((o, i) => (
              <div
                key={o.value}
                role="option"
                aria-selected={o.value === value}
                title={o.title}
                data-idx={i}
                className={
                  'oe-combo-opt' +
                  (i === activeIdx ? ' active' : '') +
                  (o.value === value ? ' sel' : '')
                }
                // onMouseDown, not onClick: it fires before the input's onBlur
                // so the pick lands instead of the list closing under the cursor.
                onMouseDown={(e) => {
                  e.preventDefault();
                  pick(o);
                }}
                onMouseEnter={() => setActiveIdx(i)}
              >
                <span className="oe-combo-label">{o.label}</span>
                {o.badge && (
                  <span className={'oe-status sm ' + o.badge.toLowerCase()}>{o.badge}</span>
                )}
              </div>
            ))
          )}
        </div>
      )}
    </div>
  );
}
